import { useContext, useEffect } from 'react'
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom'

import { env } from '@/services/EnvironmentVariables'
import { AppContext, AppContextType } from '@/AppContext'
import { UserContext, UserContextType } from '@/UserContext'
import { large_screenshare_warning_width_threshold } from '@/services/Constants'
import MediaSelectionResolver from '@/components/MediaSelectionResolver'
import useMuseDAWAudioSelectionManager from '@/services/useMuseDAWAudioSelectionManager'
import useMediaSourcesChangedEventHandlerService from '@/functionalities/MediaSources/useMediaSourcesChangedEventHandlerService'
import NewAndRemovedMediaDevicePromptController from '@/dialogs/NewAndRemovedMediaDevicePromptController'
import NewSessionInvitationDialogController from '@/dialogs/NewSessionInvitationDialogController'
import RoomController from '@/services/RoomController'
import LocalLoopbackRenderer from '@/services/LocalLoopbackRenderer'
import RoomAppUpgradeRequiredDialog from '@/dialogs/RoomAppUpgradeRequiredDialog'
import useMidiPassthroughService from '@/services/useMidiPassthroughService'
import VbAudioCableAutoDeselectionDialogController from '@/dialogs/VbAudioCableAutoDeselectionDialogController'
import useSystemAudioOutputChangeConfirmationController from '@/services/useSystemAudioOutputChangeConfirmationService'
import useMediaDeviceStatusService from '@/services/useMediaDeviceStatusService'
import useGlobalOutputDeviceService from '@/app-services/useGlobalOutputDeviceService'
import useMuseReceivePluginService from '@/app-services/useMuseReceivePluginService'
import ScreenRecordingSavingDialogController from '@/functionalities/ScreenRecording/ScreenRecordingSavingDialogController'
import useWindowsMidiService from '@/app-services/useWindowsMidiService'
import AppNotificationSound from '@/functionalities/AppNotification/AppNotificationSound'
import useAppNotificationActions from '@/functionalities/AppNotification/useAppNotificationActions'
import useAudioSettingsStoreEffects from '@/core/useAudioSettingsStoreEffects'
import useAudioEngineService from '@/services/useAudioEngineService'
import AudioEngineSampleRateMismatchDialogController from '@/dialogs/AudioEngineSampleRateMismatchDialogController'
import AudioEngineStreamUnresponsiveDialogController from '@/dialogs/AudioEngineStreamUnresponsiveDialogController'
import UpgradeToMuseProDialogController from '@/dialogs/UpgradeToMuseProDialogController'
import DeviceResolutionErrorDialogController from '@/dialogs/DeviceResolutionErrorDialogController'
import FreeTrialInfoDialogController from '@/dialogs/FreeTrialInfoDialogController'
import useAutoSelectOnMediaDeviceDisconnect from '@/services/useAutoSelectOnMediaDeviceDisconnect'
import ChooseVirtualBackgroundModal from '@/dialogs/ChooseVirtualBackgroundModal'
import { parseJoinRoomLink } from '@/services/JoinRoomLink'
import { useInIframe } from '@/services/useInIframe'
import { TooManyHostedSessionsErrorModal } from '@/components/TooManyHostedSessionsErrorModal'
import ScreenRecordingConversionDialogController from '@/dialogs/ScreenRecordingConversionDialog'

import MediaPermissionsChecklistModal from '@/pages/MediaPermissionsChecklistModal'
import ScreenControlDiagnosticsModalController from '@/pages/ScreenControlDiagnosticsPage/ScreenControlDiagnosticsModalController'
import ScreenShareSelectionModalController from '@/pages/ScreenShareSelectionPage/ScreenShareSelectionModalController'
import AudioEngineDevSettingsPageController from '@/pages/AudioEngineDevSettingsPage/AudioEngineDevSettingsPageController'
import ScheduleSessionPageController from '@/pages/ScheduleSessionPage/ScheduleSessionPageController'
import { GuestJoinPage } from '@/pages/UnauthorizedPages/GuestJoinPage'
import PerfStatsPageController from '@/pages/PerfStatsPage/PerfStatsPageController'
import { DashboardPage } from '@/pages/DashboardPage/DashboardPage'
import { HostSessionPage } from '@/pages/HostOrJoinSessionPages/HostSessionPage'
import { JoinSessionPage } from '@/pages/HostOrJoinSessionPages/JoinSessionPage'
import SessionPage from '@/pages/SessionPage/SessionPage'
import { AppSettingsDialog } from '@/components/AppSettings/AppSettingsDialog'
import StateTriggersDialogController from '@/pages/StateTriggersPage/StateTriggersDialogController'
import EnableSystemAccessPage from '@/pages/EnableSystemAccessPage/EnableSystemAccessPage'
import WelcomeToMusePage from '@/pages/WelcomeToMusePage/WelcomeToMusePage'
import MainCommunityPage from '@/pages/MainCommunityPage'

function AuthenticatedApp() {
    const history = useHistory()
    const location = useLocation()

    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state } = useContext(UserContext) as UserContextType

    useMuseDAWAudioSelectionManager()

    useMediaSourcesChangedEventHandlerService()

    useAutoSelectOnMediaDeviceDisconnect()

    useMidiPassthroughService()

    useSystemAudioOutputChangeConfirmationController()

    useMediaDeviceStatusService()

    useGlobalOutputDeviceService()

    useMuseReceivePluginService()

    useWindowsMidiService()

    useAudioSettingsStoreEffects()

    const test = useAppNotificationActions()
    useEffect(() => {
        const desktopId = state.mediaSelection.desktop
        if (desktopId && desktopId !== '_undef' && desktopId != '_none') {
            const desktop = state.mediaSources.desktop.filter((a) => a.id === desktopId)[0]
            if (desktop && desktop.screenWidth && desktop.screenWidth > large_screenshare_warning_width_threshold) {
                console.log(
                    `showing "hq_screenshare_notification" dialog because the user selected screen share with ` +
                        `screen width=${desktop.screenWidth} > ${large_screenshare_warning_width_threshold}.`
                )
                appDispatch({
                    type: 'new_message_dialog',
                    value: {
                        id: 'hq_screenshare_notification',
                        text:
                            'Sharing large or high-resolution screens may affect the performance of the app ' +
                            "depending on your computer's processing power.",
                        negativeButtonDoNotShowAgain: true,
                    },
                })
            }
        }
    }, [state.mediaSelection.desktop])

    const inIframe = useInIframe()
    useEffect(() => {
        const { code, clientType } = parseJoinRoomLink(env.JOIN_ROOM_LINK || window.location.toString())

        const onJoinPage = window.location.pathname.includes('/join')

        // If the user is already logged in with a real account, send them to the /join page
        if (code && state.userUrn && !state.isGuest && clientType !== 'listenonly' && !inIframe && !onJoinPage) {
            const url = new URL(env.JOIN_ROOM_LINK || window.location.toString())
            const params = url.searchParams
            params.delete('code')
            history.replace(`/join/${code}?${params.toString()}`)
        }
    }, [])

    return (
        <>
            <Switch location={location}>
                <Route key={'/guest-join'} path={'/guest-join'} exact={true}>
                    <GuestJoinPage />
                </Route>
                <Route key={'/welcome-to-muse'} path={'/welcome-to-muse'} exact={true}>
                    <WelcomeToMusePage />
                </Route>
                <Route key={'/'} path={'/'} exact={true}>
                    <DashboardPage key="/" />
                </Route>
                <Route key={'/host'} path={'/host'} exact={true}>
                    <HostSessionPage key="/host" />
                </Route>
                <Route key={'/join'} path={'/join/:code?'} exact={true}>
                    <JoinSessionPage key="/join/:code" />
                </Route>
                <Route key={'/collaborate'} path={'/collaborate'} exact={true}>
                    <SessionPage key="/collaborate" />
                </Route>
                <Route key={'/pages/main-community-page'} path={'/pages/main-community-page'} exact={true}>
                    <MainCommunityPage />
                </Route>
                <Route
                    key={'/pages/permissions-checklist-page'}
                    path={'/pages/permissions-checklist-page'}
                    exact={true}
                >
                    <EnableSystemAccessPage />
                </Route>
                <Redirect to="/" />
            </Switch>
            <ChooseVirtualBackgroundModal />
            {state.clientType !== 'listenonly' && (
                <>
                    <MediaSelectionResolver />
                    <NewAndRemovedMediaDevicePromptController />
                </>
            )}
            <NewSessionInvitationDialogController />
            <RoomController />
            <LocalLoopbackRenderer />
            <RoomAppUpgradeRequiredDialog />
            <VbAudioCableAutoDeselectionDialogController />
            <MediaPermissionsChecklistModal />
            <ScreenRecordingSavingDialogController />
            <AppNotificationSound />
            <ScreenControlDiagnosticsModalController />
            <AudioEngineDevSettingsPageController />
            <PerfStatsPageController />
            <ScreenShareSelectionModalController />
            <ScheduleSessionPageController />
            <AudioEngineSampleRateMismatchDialogController />
            <AudioEngineStreamUnresponsiveDialogController />
            <AudioEngineService />
            <UpgradeToMuseProDialogController />
            <DeviceResolutionErrorDialogController />
            <FreeTrialInfoDialogController />
            <AppSettingsDialog />
            <TooManyHostedSessionsErrorModal />
            <ScreenRecordingConversionDialogController />
            <StateTriggersDialogController />
        </>
    )
}

function AudioEngineService() {
    useAudioEngineService()
    return null
}

export default AuthenticatedApp
